import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RecordTable, { RecordColumn } from "../common/RecordTable";
import PrescriptionStatusBadge from "../prescriptionTab/PrescriptionStatusBadge";
import { formatDisplayDate } from "../../../../common/utils/date";
import { ServiceHistoryRow } from "../../entities/history";
import HistoryBanner from "./HistoryBanner";
import useHistoryScope from "./useHistoryScope";
import { useHistoryToolbarSlots } from "./HistoryToolbar";
import { useServiceHistory } from "./useServiceHistory";
import { historyLoadingRestText } from "./useHistoryList";

/**
 * History → Programming: the services a person has been prescribed over time
 * (design nodes 16535:92175 for the affiliate, 16591:64831 for the customer).
 *
 * Loaded whole from `GET /history/services` — see `useServiceHistory` and
 * `useHistoryList` for why every page is fetched up front. Same `RecordTable`
 * as the other two tabs, so the chrome and the header filters are shared.
 */
const STATUS_LABEL: Record<ServiceHistoryRow["status"], string> = {
  active: "Active",
  expired: "Expired",
  inactive: "Inactive",
};

const ProgrammingHistoryTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scope = useHistoryScope();
  const toolbarSlots = useHistoryToolbarSlots();
  const { rows, loading, loadingRest, totalCount, truncated, error } =
    useServiceHistory(scope);

  const showClient = !scope.isSingleSubject;

  const columns = useMemo<RecordColumn<ServiceHistoryRow>[]>(
    () => [
      {
        key: "dateCreated",
        label: "Date created",
        flex: 1,
        filter: "date",
        date: (row) => row.dateCreated,
        value: (row) => formatDisplayDate(row.dateCreated),
      },
      ...(showClient
        ? [
            {
              key: "client",
              label: "Client",
              flex: 1,
              searchable: true,
              withInitials: true,
              optionKey: (row: ServiceHistoryRow) => row.clientId,
              value: (row: ServiceHistoryRow) => row.clientName,
            } as RecordColumn<ServiceHistoryRow>,
          ]
        : []),
      {
        key: "serviceType",
        label: "Type",
        flex: 1,
        value: (row) => row.serviceType,
      },
      {
        key: "serviceName",
        label: "Service",
        flex: 1,
        value: (row) => row.serviceName,
      },
      {
        key: "status",
        label: "Status",
        flex: 1,
        value: (row) => STATUS_LABEL[row.status],
        optionKey: (row) => row.status,
        render: (row) => (
          <PrescriptionStatusBadge
            isActive={row.status === "active"}
            isExpired={row.status === "expired"}
          />
        ),
      },
    ],
    [showClient]
  );

  const banners = (
    <>
      {error && (
        <HistoryBanner tone="warning" text={error} />
      )}
      {loadingRest && (
        <HistoryBanner tone="info" text={historyLoadingRestText(rows.length, totalCount)} />
      )}
      {truncated && (
        <HistoryBanner tone="info" text="Only the most recent services are shown." />
      )}
    </>
  );

  return (
    <RecordTable<ServiceHistoryRow>
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={loading}
      banners={banners}
      skeletonRows={6}
      clearFiltersSlot={toolbarSlots.filters}
      persistKey={`history:${scope.kind}:programming`}
      emptyMessage="No services found"
      filteredEmptyMessage="No services match these filters"
      onRowClick={(row) =>
        navigate(`${scope.basePath}/programming/${row.id}${location.search}`)
      }
    />
  );
};

export default ProgrammingHistoryTab;
